import { useCallback, useMemo, useState } from 'react';
import { postAuthMeSelectTrainerBranch } from '../../../entities/me/me.api';
import { useAuth } from '../../../shared/auth/use-auth';
import { userFacingActionError } from '../../../shared/lib/staff-user-facing-error';
import { cn } from '../../../shared/lib/cn';
import { useStaffToast } from '../../../shared/ui/StaffToast';
import { trainerNeedsActiveBranchPicker, uniqueTrainerPlacementBranches } from '../lib/trainer-active-branch';

const SELECT_CLASS =
  'h-9 w-full min-w-[10rem] max-w-[20rem] appearance-none rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted))] px-2.5 pr-8 text-sm text-[hsl(var(--ui-foreground))] outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]';

type TrainerActiveBranchPickerProps = {
  className?: string;
  /** После успешного POST и `refreshMe` (например, повторная загрузка списка). */
  onApplied?: () => void;
};

/**
 * Выбор активного филиала для тренера с несколькими площадками (`POST /auth/me/select-trainer-branch` + `refreshMe`).
 */
export function TrainerActiveBranchPicker({ className, onApplied }: TrainerActiveBranchPickerProps) {
  const { me, apiClient, refreshMe } = useAuth();
  const toast = useStaffToast();
  const [busy, setBusy] = useState(false);

  const branches = useMemo(() => uniqueTrainerPlacementBranches(me), [me]);
  const show = trainerNeedsActiveBranchPicker(me);

  const currentId =
    me?.active_branch_id != null && me.active_branch_id > 0 ? me.active_branch_id : null;
  const selectValue = currentId != null && branches.some((b) => b.branch_id === currentId) ? String(currentId) : '';

  const applyBranch = useCallback(
    async (branchId: number) => {
      if (branchId <= 0) return;
      if (currentId === branchId) return;
      setBusy(true);
      try {
        await postAuthMeSelectTrainerBranch(apiClient, { branch_id: branchId });
        const next = await refreshMe();
        if (!next) {
          toast({ variant: 'error', message: 'Филиал сохранён, но не удалось обновить сессию.' });
          return;
        }
        toast({ variant: 'success', message: 'Активный филиал обновлён.' });
        onApplied?.();
      } catch (e) {
        toast({ variant: 'error', message: userFacingActionError(e) });
      } finally {
        setBusy(false);
      }
    },
    [apiClient, currentId, onApplied, refreshMe, toast]
  );

  if (!show) return null;

  return (
    <div
      className={cn('flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center', className)}
      data-testid="trainer-active-branch-picker"
    >
      <label className="flex min-w-0 flex-col gap-1 sm:max-w-none">
        <span className="text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">Активный филиал</span>
        <div className="relative inline-block w-full sm:w-auto">
          <select
            className={SELECT_CLASS}
            disabled={busy}
            value={selectValue}
            aria-label="Активный филиал для работы"
            onChange={(ev) => {
              const v = ev.target.value;
              const id = v ? Number(v) : NaN;
              if (!Number.isFinite(id) || id <= 0) return;
              void applyBranch(id);
            }}
          >
            <option value="">Выберите филиал…</option>
            {branches.map((b) => (
              <option key={b.branch_id} value={String(b.branch_id)}>
                {b.branch_name}
              </option>
            ))}
          </select>
        </div>
      </label>
    </div>
  );
}
